import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { OpportunityVisibility } from 'Event/interfaces'
import { FormModal } from 'components/FormModal'
import { DEFAULT_GEOFENCING } from 'civic-champs-shared/constants/GEO_DATA'
import { EventInfoScreen, EventInfoScreenValues } from 'Event/components/EventInfoScreen'
import { useGroups } from 'group/hooks/useGroups'
import { Group } from 'Event/components/opportunity/GroupPicker/types'
import { EventGeofencing, mapEventGeofencingToGeofencing, mapGeofencingToEventGeofencing } from 'utils/event'
import { useEnabledGroups } from 'Event/components/opportunity/GroupPicker/hooks'
import { AssociationType, EventGroup } from 'Event/interfaces/interfaceCreateEditEvent'
import Loading from 'civic-champs-shared/core/Loading'
import {
  OpportunityTemplate,
  OpportunityTemplatePayload,
  OpportunityTemplatePersonGroupPayload,
  OpportunityTemplateRolePayload,
} from 'volunteering/opportunities/interfaces'
import { useGetOpportunityTemplateGroups } from 'volunteering/opportunities/hooks/useGetOpportunityTemplateGroups'
import { useFeatureEnabled } from 'core/feature/hooks'
import {
  FormReferenceItem,
  mapToEventGroup,
  toFormRole,
  toLocalDate,
  toLocalTime,
} from 'Event/helpers/add-edit-helpers'
import useGetOpportunityTemplateSurveys from 'volunteering/opportunities/hooks/useGetOpportunityTemplateSurveys'
import useGetOpportunityTemplateCredentials from 'volunteering/opportunities/hooks/useGetOpportunityTemplateCredentials'
import { Geofencing } from 'civic-champs-shared/core/location/utils'
import {
  deriveAddressTypeFromTemplate,
  mapTemplateCustomLocationToApiPayload,
} from '../../locations/helpers/location-address'
import { LocationAddressType } from '../../locations/types'
import {
  EventRegistrationDetailsScreen,
  EventRegistrationDetailsScreenValues,
} from 'Event/components/EventRegistrationDetailsScreen'
import { useLocationsCollection } from 'locations/hooks'
import { isUndefined } from 'lodash'
import { Person } from '../../people/interface'
import map from 'lodash/map'
import { useRolesCollection } from 'volunteer-role/hooks'
import { TemplateTimeAndRoleScreen, TemplateTimeAndRoleScreenValues } from './TemplateTimeAndRoleScreen'

interface Props {
  showing: boolean
  complete: (result: {
    values: OpportunityTemplatePayload
    groups: Omit<OpportunityTemplatePersonGroupPayload, 'opportunityTemplateId'>[]
    questionnaires: number[]
    waivers: number[]
  }) => void
  close: () => void
  opportunityTemplate?: OpportunityTemplate
  additionalData?: {
    questionnaires: FormReferenceItem[]
    waivers: FormReferenceItem[]
    groups: EventGroup[]
  }
}

interface FormData
  extends Omit<OpportunityTemplatePayload, 'geofencing' | 'pointOfContactPersonIds' | 'startTime' | 'endTime'> {
  groups: EventGroup[]
  questionnaires: FormReferenceItem[]
  waivers: FormReferenceItem[]
  geofencing: Geofencing
  locationIsAddress: boolean
  pointOfContactPersons?: Person[]
  useCustomAddress: boolean
  customAddressType?: LocationAddressType
  plusCode?: string
  locationAddressType?: LocationAddressType
  startTime: Date | null
  endTime: Date | null
}

const defaultValues: Omit<FormData, 'isSchedulable'> = {
  name: '',
  description: '',
  isActive: true,
  geofencing: DEFAULT_GEOFENCING,
  streetAddress: '',
  city: '',
  state: '',
  zip: '',
  visibility: OpportunityVisibility.PUBLIC,
  groups: [],
  questionnaires: [],
  waivers: [],
  locationDetails: '',
  isRecurring: false,
  recurrencePattern: null,
  startTime: null,
  endTime: null,
  roles: [],
  isPrivate: false,
  contactName: '',
  contactEmail: '',
  contactPhoneNumber: '',
  instructions: '',
  locationIsAddress: true,
  organizationLocationId: null,
  pointOfContactPersons: [],
  useCustomAddress: false,
  customAddressType: LocationAddressType.AddressAndCoords,
  plusCode: '',
}

const mapFormDataToTimeAndRoleScreenValues = (formData?: FormData): TemplateTimeAndRoleScreenValues => {
  const values = formData || defaultValues
  return {
    name: values.name,
    isActive: values.isActive,
    startTime: values.startTime,
    endTime: values.endTime,
    roles: map(values.roles, ({ volunteeringRoleId, quantity }) => toFormRole({ volunteeringRoleId, quantity })),
  }
}

const mapFormDataToRegistrationScreenValues = (formData?: FormData): EventRegistrationDetailsScreenValues => {
  const values = formData || defaultValues
  return {
    onboardingGroups: values.groups.filter(
      ({ associationType }) => associationType === AssociationType.ADD_PARTICIPANTS_TO_GROUP,
    ),
    questionnaires: values.questionnaires,
    waivers: values.waivers,
    instructions: values.instructions || '',
    visibility: values.visibility,
  }
}

const mapFormDataToEventScreenValues = (formData?: FormData): EventInfoScreenValues => {
  const values = formData || defaultValues
  const customAddressType = values.customAddressType ?? deriveAddressTypeFromTemplate(values)
  const isPlusCode = customAddressType === LocationAddressType.PlusCode

  return {
    name: values.name,
    description: values.description || '',
    address: isPlusCode ? '' : values.streetAddress || '',
    city: isPlusCode ? '' : values.city || '',
    state: isPlusCode ? '' : values.state || '',
    zip: isPlusCode ? '' : values.zip || '',
    visibility: values.visibility,
    geofencing: values.geofencing ?? DEFAULT_GEOFENCING,
    visibilityGroups: values.groups.filter(
      ({ associationType }) => associationType === AssociationType.EVENT_PRIVATE_TO_MEMBERS,
    ),
    locationIsAddress: values.locationIsAddress,
    useCustomAddress: values.useCustomAddress,
    locationDetails: values.locationDetails || '',
    isActive: values.isActive,
    contactEmail: values.contactEmail || '',
    contactName: values.contactName || '',
    contactPhoneNumber: values.contactPhoneNumber || '',
    organizationLocationId: values.organizationLocationId || null,
    pointOfContactPersons: values.pointOfContactPersons,
    customAddressType,
    locationAddressType: values.locationAddressType ?? customAddressType,
    plusCode: isPlusCode ? values.plusCode || values.streetAddress || '' : '',
  }
}

const mapOpportunityTemplateToFormData = (
  { geofencing, startTime, endTime, roles: templateRoles, ...opportunityTemplate }: OpportunityTemplate,
  groups: EventGroup[],
  questionnaires: FormReferenceItem[],
  waivers: FormReferenceItem[],
): FormData => ({
  ...opportunityTemplate,
  geofencing: geofencing ? mapEventGeofencingToGeofencing(geofencing as EventGeofencing) : DEFAULT_GEOFENCING,
  groups,
  questionnaires,
  waivers,
  useCustomAddress: !opportunityTemplate.organizationLocationId,
  customAddressType: deriveAddressTypeFromTemplate(opportunityTemplate),
  plusCode:
    opportunityTemplate.locationAddressType === LocationAddressType.PlusCode
      ? opportunityTemplate.streetAddress || ''
      : '',
  locationAddressType: opportunityTemplate.locationAddressType,
  startTime: toLocalDate(startTime),
  endTime: toLocalDate(endTime),
  roles: map(templateRoles, ({ role, quantity }) => ({ volunteeringRoleId: role.id, quantity })),
})

const mapTimeAndRoleValuesToFormData = ({
  isActive,
  roles: formRoles,
  ...values
}: TemplateTimeAndRoleScreenValues): Partial<FormData> => ({
  ...values,
  isActive: isActive || false,
  roles: map(
    formRoles,
    ({ roleId, quantity }) => ({ volunteeringRoleId: roleId, quantity } as OpportunityTemplateRolePayload),
  ),
})

const mapInfoValuesToFormData = (
  { address, visibilityGroups, plusCode, customAddressType, locationAddressType, ...values }: EventInfoScreenValues,
  groups: EventGroup[],
): Partial<FormData> => ({
  ...values,
  isActive: values.isActive || false,
  streetAddress: address,
  plusCode: plusCode || '',
  customAddressType,
  locationAddressType,
  isPrivate: values.visibility === OpportunityVisibility.PRIVATE,
  groups: [
    ...groups.filter(({ associationType }) => associationType !== AssociationType.EVENT_PRIVATE_TO_MEMBERS),
    ...visibilityGroups,
  ],
})

const mapEventGroupToOpportunityTemplateGroup = (
  group: EventGroup,
): Omit<OpportunityTemplatePersonGroupPayload, 'opportunityTemplateId'> => ({
  groupId: group.groupId,
  associationType: group.associationType,
  approvedMembersOnly: group.approvedMembersOnly,
})

export const AddEditOpportunityTemplatePrompt = ({
  showing,
  close,
  complete,
  opportunityTemplate,
  additionalData,
}: Props) => {
  const [additionalDataLoading, setAdditionalDataLoading] = useState(
    !isUndefined(opportunityTemplate) && isUndefined(additionalData),
  )
  const { groups: initialGroups, loading: groupsLoading } = useGroups() as { groups: Group[]; loading: boolean }
  const groups = useEnabledGroups(initialGroups)
  const [{ locations, loading: locationsLoading, initiallyLoaded: locationsLoaded }] = useLocationsCollection()
  const namedLocations = useMemo(() => {
    return locations.filter(({ name }) => name !== null)
  }, [locations])
  const [{ roles, initiallyLoaded: rolesLoaded }, { addRole }] = useRolesCollection({ reporting: false })
  const [getOpportunityTemplateGroups] = useGetOpportunityTemplateGroups()
  const [getOpportunityTemplateSurveys] = useGetOpportunityTemplateSurveys()
  const [getOpportunityTemplateCredentials] = useGetOpportunityTemplateCredentials()
  const isCalendarEventSwitchEnabled = useFeatureEnabled('NewAddEventUICalendarEvent')
  const isSchedulable = useMemo(
    () => (opportunityTemplate ? opportunityTemplate.isSchedulable : isCalendarEventSwitchEnabled),
    [isCalendarEventSwitchEnabled, opportunityTemplate],
  )
  const [formData, setFormData] = useState<FormData>()
  const [screen, setScreen] = useState<number>(0)

  const [timeAndRoleValues, eventInfoValues, registrationDetailsValues] = useMemo(
    () => [
      mapFormDataToTimeAndRoleScreenValues(formData),
      mapFormDataToEventScreenValues(formData),
      mapFormDataToRegistrationScreenValues(formData),
    ],
    [formData],
  )

  const getLocationId = () => {
    if (namedLocations.some(location => location.id === opportunityTemplate?.organizationLocationId)) {
      return opportunityTemplate?.organizationLocationId as number
    } else {
      return null
    }
  }

  useEffect(() => {
    if (!locationsLoaded) return
    if (opportunityTemplate) {
      if (!isUndefined(opportunityTemplate) && isUndefined(additionalData)) {
        Promise.all([
          getOpportunityTemplateGroups(opportunityTemplate.id),
          getOpportunityTemplateSurveys(opportunityTemplate.id),
          getOpportunityTemplateCredentials(opportunityTemplate.id),
        ]).then(([opportunityTemplateGroups, opportunityTemplateSurveys, opportunityTemplateCredentials]) => {
          setFormData({
            ...mapOpportunityTemplateToFormData(
              opportunityTemplate,
              opportunityTemplateGroups.map(mapToEventGroup),
              opportunityTemplateSurveys.map(({ survey: { questionSetName, id } }) => ({ name: questionSetName, id })),
              opportunityTemplateCredentials.map(({ credential: { id, name } }) => ({ name, id })),
            ),
            organizationLocationId: getLocationId(),
          })
          setAdditionalDataLoading(false)
        })
      } else {
        const organizationLocationId = getLocationId()
        setFormData({
          ...mapOpportunityTemplateToFormData(
            opportunityTemplate,
            additionalData?.groups || [],
            additionalData?.questionnaires || [],
            additionalData?.waivers || [],
          ),
          organizationLocationId,
          useCustomAddress: !organizationLocationId,
        })
      }
    } else {
      setFormData({ ...defaultValues, isSchedulable: isCalendarEventSwitchEnabled })
    }
  }, [locationsLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  //TODO: originally only could receive  EventRegistrationDetailsScreenValues (was single step)
  const onBackFromEventInfo = useCallback((data: EventInfoScreenValues) => {
    setScreen(idx => idx - 1)
    setFormData(prev => ({ ...prev, ...(mapInfoValuesToFormData(data, prev?.groups || []) as FormData) }))
  }, [])

  const onBackFromRegDetails = useCallback(({ onboardingGroups, ...values }: EventRegistrationDetailsScreenValues) => {
    setScreen(idx => idx - 1)
    setFormData(
      data =>
        ({
          ...data,
          ...values,
          groups: [
            ...(data?.groups.filter(
              ({ associationType }) => associationType !== AssociationType.ADD_PARTICIPANTS_TO_GROUP,
            ) || []),
            ...onboardingGroups,
          ],
        } as FormData),
    )
  }, [])

  //TODO check and see if using the "mutation" form of `useState()` setter causes problems or not
  const handleTimeAndRoleSubmit = useCallback((data: TemplateTimeAndRoleScreenValues) => {
    setFormData(prev => ({ ...prev, ...(mapTimeAndRoleValuesToFormData(data) as FormData) }))
    setScreen(idx => idx + 1)
  }, [])

  const handleInfoSubmit = useCallback((data: EventInfoScreenValues) => {
    setFormData(prev => ({ ...prev, ...(mapInfoValuesToFormData(data, prev?.groups || []) as FormData) }))
    setScreen(idx => idx + 1)
  }, [])

  const handleSubmit = useCallback(
    ({ onboardingGroups, ...values }: EventRegistrationDetailsScreenValues) => {
      const {
        groups,
        questionnaires,
        waivers,
        geofencing,
        pointOfContactPersons,
        startTime,
        endTime,
        useCustomAddress,
        organizationLocationId,
        streetAddress,
        city,
        state,
        zip,
        plusCode,
        customAddressType,
        locationDetails,
        ...payload
      } = {
        ...formData,
        ...values,
      } as FormData

      const locationPayload = mapTemplateCustomLocationToApiPayload({
        useCustomAddress,
        organizationLocationId: organizationLocationId || null,
        streetAddress: streetAddress || '',
        city: city || '',
        state: state || '',
        zip: zip || '',
        plusCode,
        customAddressType,
        locationDetails: locationDetails || '',
        geofencing: geofencing as Geofencing,
      })

      complete({
        values: {
          ...payload,
          ...locationPayload,
          geofencing: locationPayload.geofencing
            ? mapGeofencingToEventGeofencing(locationPayload.geofencing as Geofencing)
            : null,
          pointOfContactPersonIds: map(pointOfContactPersons, 'id'),
          startTime: toLocalTime(startTime),
          endTime: toLocalTime(endTime),
        },
        groups: [
          ...groups.filter(({ associationType }) => associationType !== AssociationType.ADD_PARTICIPANTS_TO_GROUP),
          ...onboardingGroups,
        ].map(mapEventGroupToOpportunityTemplateGroup),
        questionnaires: questionnaires.map(({ id }) => id),
        waivers: waivers.map(({ id }) => id),
      })
    },
    [complete, formData],
  )

  const isRolesInTemplatesEnabled = useFeatureEnabled('RolesInEventTemplates')

  const showRolesScreen = isRolesInTemplatesEnabled && isSchedulable
  const screens = [
    <EventInfoScreen
      values={eventInfoValues}
      onBack={isRolesInTemplatesEnabled ? onBackFromEventInfo : close}
      isNewEvent={!opportunityTemplate}
      onSubmit={handleInfoSubmit}
      groups={groups}
      isTemplate={true}
      isDraftable={false}
      isSchedulable={isSchedulable}
      namedLocations={namedLocations}
    />,
    <EventRegistrationDetailsScreen
      values={registrationDetailsValues}
      onBack={onBackFromRegDetails}
      isNewEvent={!opportunityTemplate}
      isDraftable={false}
      onSubmit={handleSubmit}
      groups={groups}
      isSchedulable={isSchedulable}
      showPublishTime={false}
    />,
  ]

  if (showRolesScreen) {
    screens.unshift(
      <TemplateTimeAndRoleScreen
        values={timeAndRoleValues}
        onBack={close}
        onSubmit={handleTimeAndRoleSubmit}
        roles={roles}
        addRole={addRole}
      />,
    )
  }

  const loading = groupsLoading || additionalDataLoading || locationsLoading || !rolesLoaded

  return (
    <FormModal
      id="opportunity-template-prompt"
      showing={showing}
      close={close}
      title={(opportunityTemplate ? 'Edit' : 'Add') + ' Event Template'}
    >
      {loading ? <Loading /> : screens[screen]}
    </FormModal>
  )
}

export default AddEditOpportunityTemplatePrompt
